import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { type DialogState } from 'ariakit'
import {
  Button,
  IconArrowsClockwise,
  IconTag,
  IconXOctagon,
} from '@vtex/admin-ui'

import * as styles from './UploadFileArea.styles'

interface UploadFileAreaProps {
  hasFile: File | null
  setHasFile: (file: File | null) => void
  onUploadFiles?: (file: File) => void
  innerButton: React.ReactNode
  modalState: DialogState
  handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  isCorrectFormat: boolean
}

export interface UploadFileAreaRef {
  openUploader: () => void
}

export const UploadFileArea = forwardRef<
  UploadFileAreaRef,
  UploadFileAreaProps
>(function UploadFilesArea(
  {
    onUploadFiles,
    innerButton,
    modalState,
    handleClick,
    hasFile,
    setHasFile,
    isCorrectFormat,
  },
  ref
) {
  const { formatMessage } = useIntl()
  const [isDragging, setIsDragging] = useState(false)
  const uploadInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!modalState.open) {
      setHasFile(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState])

  useImperativeHandle(ref, () => ({
    openUploader: () => {
      uploadInputRef.current?.click()
    },
  }))

  const handleDragEvent =
    (isDragging: boolean) => (event: React.SyntheticEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setIsDragging(isDragging)
    }

  const handleUploadFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return

    const file = files[0]
    setHasFile(file)
    onUploadFiles?.(file)

    if (uploadInputRef.current) {
      uploadInputRef.current.value = ''
    }
  }

  return (
    <label
      className={`${isDragging} ${!isCorrectFormat ? 'invalid-format' : ''} 
      ${!hasFile ? 'empty-upload' : ''} ${styles.uploadAreaWrapper}`}
      onDragEnter={handleDragEvent(true)}
      onDragOver={handleDragEvent(true)}
      onDragLeave={handleDragEvent(false)}
      onDrop={(event) => {
        handleDragEvent(false)(event)
        handleUploadFiles(event.dataTransfer.files)
      }}
    >
      <div>
        {hasFile && isCorrectFormat && (
          <div className={styles.uploadedFile}>
            <div>
              <div className={styles.iconWrapper}>
                <IconTag />
              </div>
              {hasFile.name}
            </div>
            <Button
              aria-label={formatMessage({
                id: 'admin/admin.catalog.import-modal.upload-area.invalid-format-action',
                defaultMessage: 'Choose another file',
              })}
              icon={<IconArrowsClockwise />}
              variant="neutralTertiary"
              onClick={(event) => handleClick(event)}
            ></Button>
          </div>
        )}
        {hasFile && !isCorrectFormat && (
          <div className={styles.uploadedFile}>
            <div>
              <div className={`${styles.iconWrapper} ${styles.iconInvalid}`}>
                <IconXOctagon />
              </div>
              {formatMessage({
                id: 'admin/admin.catalog.import-modal.upload-area.invalid-format-message',
                defaultMessage: 'Invalid format',
              })}
            </div>
            <Button
              variant="neutralTertiary"
              onClick={(event) => handleClick(event)}
            >
              {formatMessage({
                id: 'admin/admin.catalog.import-modal.upload-area.invalid-format-action',
                defaultMessage: 'Choose another file',
              })}
            </Button>
          </div>
        )}
        {!hasFile && innerButton}
        <input
          ref={uploadInputRef}
          onChange={(event) => handleUploadFiles(event.target.files)}
          type="file"
          accept="application/vnd.ms-excel, 
          application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .xls, .xlsx"
          multiple={false}
          hidden
        />
      </div>
    </label>
  )
})
